import { useState } from 'react'
import { PiCirclesThreePlus, PiProhibit } from 'react-icons/pi'

import { CompanyHeader } from './CompanyHeader'
import { PillTabs } from 'components/FbUI/PillTabs'
import { CompanySidebarFooter } from './CompanySidebarFooter'
import { CustomCompanySidebarFooter } from './CustomCompanySidebarFooter'
import { cn } from 'components/UI/cn'
import type { CompanySidebarHeader, SidebarTabConfig } from './index'

interface SidebarContentProps {
  chainProxyId: number
  header: CompanySidebarHeader
  tabs: SidebarTabConfig[]
  doorId?: number
  address?: string
  phone?: string
}

/**
 * Sidebar content with custom company banner, header, tabs, content area, and footer.
 */
export function SidebarContent({
  chainProxyId,
  header,
  tabs,
  doorId,
  address,
  phone,
}: SidebarContentProps) {
  const [selectedTab, setSelectedTab] = useState(tabs[0]?.key || '')

  const currentTabContent =
    tabs.find((tab) => tab.key === selectedTab)?.content || []

  // Closure state takes precedence over the custom-company banner; a user
  // needs to know the location is closed before learning it's user-created.
  const isTemporarilyClosed = header.closedStatus === 'Temporarily closed'
  const isPermanentlyClosed = header.closedStatus === 'Permanently closed'
  const showClosedBanner = isTemporarilyClosed || isPermanentlyClosed
  const showCustomBanner = !showClosedBanner && header.isUserCreated
  const showAnyBanner = showClosedBanner || showCustomBanner

  return (
    <>
      {isTemporarilyClosed && (
        <div className="w-full absolute top-0 left-0 bg-amber-50 border-b border-amber-500 rounded-t-lg h-10 flex items-center justify-center text-amber-700 font-medium text-xs">
          <PiProhibit size={16} className="mr-1" />
          Temporarily Closed
        </div>
      )}
      {isPermanentlyClosed && (
        <div className="w-full absolute top-0 left-0 bg-red-50 border-b border-red-500 rounded-t-lg h-10 flex items-center justify-center text-red-700 font-medium text-xs">
          <PiProhibit size={16} className="mr-1" />
          Permanently Closed
        </div>
      )}
      {showCustomBanner && (
        <div className="w-full absolute top-0 left-0 bg-violet-50 border-b border-violet-500 rounded-t-lg h-10 flex items-center justify-center text-violet-700 font-medium text-xs">
          <PiCirclesThreePlus size={16} className="mr-1" />
          Custom Company
        </div>
      )}
      <div
        className={cn('flex-shrink-0', {
          'mt-4': showAnyBanner,
        })}
      >
        <div className="mb-[24px]">
          <CompanyHeader
            companyName={header.name}
            domain={header.domain}
            logoUrl={header.logoUrl}
            isUserCreated={header.isUserCreated}
          />
        </div>
        <PillTabs
          idPrefix="company-sidebar"
          aria-label="Company sidebar sections"
          tabs={tabs.map((t) => ({ key: t.key, title: t.title }))}
          activeKey={selectedTab}
          onChange={setSelectedTab}
          className="w-full [&>button]:flex-1"
        />
      </div>

      <div className="flex-1 overflow-y-auto min-h-0 pr-3">
        <div className="py-6 space-y-6">{currentTabContent}</div>
      </div>

      <div className="flex-shrink-0">
        {header.isUserCreated ? (
          <CustomCompanySidebarFooter chainProxyId={chainProxyId} />
        ) : (
          <CompanySidebarFooter
            chainProxyId={chainProxyId}
            companyName={header.name}
            domain={header.domain}
            doorId={doorId}
            address={address}
            phone={phone}
          />
        )}
      </div>
    </>
  )
}
